import { arrayOf, number, oneOf, shape, string } from 'prop-types';

export const AlertMessageType = {
  New: 1,
  Update: 2,
  Cancel: 3,
};

export const AlertMessageCategory = {
  Geo: 1,
  Met: 2,
  Safety: 3,
  Security: 4,
  Rescue: 5,
  Fire: 6,
  Health: 7,
  Env: 8,
  Transport: 9,
  Infra: 10,
  CBRNE: 11,
  Other: 12,
};

export const AreaType = {
  County: 'C',
  Zone: 'Z',
  CanadaLocation: 'CLC',
};

export const Certainty = {
  Observed: 1,
  Likely: 2,
  Possible: 3,
  Unlikely: 4,
  Unknown: 5,
};

export const Severity = {
  Extreme: 1,
  Severe: 2,
  Moderate: 3,
  Minor: 4,
  Unknown: 5,
};

export const Urgency = {
  Immediate: 1,
  Expected: 2,
  Future: 3,
  Past: 4,
  Unknown: 5,
};

export const FloodImmediateCause = {
  ExcessiveRainfall: 'ER',
  Snowmelt: 'SM',
  RainSnowmelt: 'RS',
  DamOrLeveeFailure: 'DM',
  IceJam: 'IJ',
  GlacierDammedLakeOutburst: 'GO',
  RainSnowmeltandIceJam: 'IC',
  UpstreamFloodingStormSurge: 'FS',
  UpstreamFloodingTidalEffects: 'FT',
  ElevatedUpstreamFlowTidalEffects: 'ET',
  WindTidalEffects: 'WT',
  UpstreamDamReservoirRelease: 'DR',
  OtherMultipleCauses: 'MC',
  OtherEffects: 'OT',
  Unknown: 'UU',
};

export const FloodRecordStatus = {
  NotExpected: 'NO',
  NearRecord: 'NR',
  NoRecord: 'UU',
  ArealFlood: 'OO',
};

export const FloodSeverity = {
  None: 'N',
  NotAvailable: '0',
  Minor: '1',
  Moderate: '2',
  Major: '3',
  Unknown: 'U',
};

export const ResponseType = {
  Shelter: 1,
  Evacuate: 2,
  Prepare: 3,
  Execute: 4,
  Avoid: 5,
  Monitor: 6,
  Assess: 7,
  AllClear: 8,
  None: 9,
};

/**
 * Response from alerts/headlines API
 * https://docs.google.com/document/d/1fhvDbnpGmptPigFkw5NbUxKtuHreyC16TtZHfyGCsBk/edit
 */
export const alertsHeadlinesType = arrayOf(
  shape({
    adminDistrict: string,
    adminDistrictCode: string,
    areaId: string.isRequired,
    areaName: string.isRequired,
    areaTypeCode: oneOf(Object.values(AreaType)).isRequired,
    categories: arrayOf(
      shape({
        category: oneOf([
          'Geo',
          'Met',
          'Safety',
          'Security',
          'Rescue',
          'Fire',
          'Health',
          'Env',
          'Transport',
          'Infra',
          'CBRNE',
          'Other',
        ]).isRequired,
        categoryCode: oneOf(Object.values(AlertMessageCategory)).isRequired,
      }),
    ),
    certainty: oneOf(['Observed', 'Likely', 'Possible', 'Unlikely', 'Unknown']).isRequired,
    certaintyCode: oneOf(Object.values(Certainty)).isRequired,
    countryCode: string.isRequired,
    countryName: string.isRequired,
    detailKey: string.isRequired,
    disclaimer: string,
    effectiveTimeLocal: string,
    effectiveTimeLocalTimeZone: string,
    eventDescription: string.isRequired,
    eventTrackingNumber: string.isRequired,
    expireTimeLocal: string.isRequired,
    expireTimeLocalTimeZone: string.isRequired,
    expireTimeUTC: number.isRequired,
    flood: shape({
      floodCrestTimeLocal: string,
      floodCrestTimeLocalTimeZone: string,
      floodEndTimeLocal: string,
      floodEndTimeLocalTimeZone: string,
      floodImmediateCause: oneOf([
        'Excessive Rainfall',
        'Snowmelt',
        'Rain and Snowmelt',
        'Dam or Levee Failure',
        'Ice Jam',
        'Glacier-Dammed Lake Outburst',
        'Rain and/or Snowmelt and/or Ice Jam',
        'Upstream Flooding plus Storm Surge',
        'Upstream Flooding plus Tidal Effects',
        'Elevated Upstream Flow plus Tidal Effects',
        'Wind and/or Tidal Effects',
        'Upstream Dam or Reservoir Release',
        'Other Multiple Causes',
        'Other Effects',
        'Unknown',
      ]),
      floodImmediateCauseCode: oneOf(Object.values(FloodImmediateCause)),
      floodLocationId: string,
      floodLocationName: string.isRequired,
      floodRecordStatus: oneOf([
        'A record flood is not expected',
        'Near record or record flood expected',
        'Flood without a period of record to compare',
        'For areal flood warnings, areal flash flood products, and flood advisories (point and areal)',
        'N/A',
      ]).isRequired,
      floodRecordStatusCode: oneOf(Object.values(FloodRecordStatus)),
      floodSeverity: oneOf(['None', 'N/A', 'Minor', 'Moderate', 'Major', 'Unknown']),
      floodSeverityCode: oneOf(Object.values(FloodSeverity)),
      floodStartTimeLocal: string,
      floodStartTimeLocalTimeZone: string,
    }),
    headlineText: string.isRequired,
    ianaTimeZone: string,
    identifier: string.isRequired,
    issueTimeLocal: string.isRequired,
    issueTimeLocalTimeZone: string.isRequired,
    latitude: number,
    longitude: number,
    messageType: string.isRequired,
    messageTypeCode: oneOf(Object.values(AlertMessageType)).isRequired,
    officeAdminDistrict: string,
    officeAdminDistrictCode: string,
    officeCode: string.isRequired,
    officeCountryCode: string,
    officeName: string,
    onsetTimeLocal: string,
    onsetTimeLocalTimeZone: string,
    phenomena: string.isRequired,
    processTimeUTC: number.isRequired,
    productIdentifier: string.isRequired,
    responseTypes: arrayOf(
      shape({
        responseType: oneOf([
          'Shelter',
          'Evacuate',
          'Prepare',
          'Execute',
          'Avoid',
          'Monitor',
          'Assess',
          'AllClear',
          'None ',
        ]).isRequired,
        responseTypeCode: oneOf(Object.values(ResponseType)).isRequired,
      }),
    ),
    severity: oneOf(['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown']).isRequired,
    severityCode: oneOf(Object.values(Severity)).isRequired,
    significance: string.isRequired,
    source: string.isRequired,
    urgency: oneOf(['Immediate', 'Expected', 'Future', 'Past', 'Unknown']).isRequired,
    urgencyCode: oneOf(Object.values(Urgency)).isRequired,
  }),
);
